import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { descargar, imprimirPdf } from "@/lib/download";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Lock, Printer, FileSpreadsheet, FileText, Unlock } from "lucide-react";

const gtDate = (iso) => new Date(iso).toLocaleDateString("en-CA", { timeZone: "America/Guatemala" });

/** Semana sábado → viernes (hora Guatemala). */
export const semanaDe = (iso) => {
  const d = new Date(`${gtDate(iso)}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 1) % 7));
  return d.toISOString().slice(0, 10);
};
const finDe = (ini) => { const d = new Date(`${ini}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + 6); return d.toISOString().slice(0, 10); };
const fmt = (s) => s.split("-").reverse().slice(0, 2).join("/");

const vigente = (o) => !o.anulado && o.estado !== "cancelado";
const qty = (o) => (o.items || []).reduce((n, it) => n + (Number(it.cantidad) || 0), 0);
export const facturado = (o) => (o.facturas || []).some((f) => f.numero_factura);

export const totalesArticulos = (orders) => {
  const v = orders.filter(vigente);
  const articulos = v.reduce((n, o) => n + qty(o), 0);
  const facturados = v.filter(facturado).reduce((n, o) => n + qty(o), 0);
  return { pedidos: v.length, articulos, facturados, pendientes: articulos - facturados };
};

/** Agrupa pedidos por semana e incluye semanas cerradas sin pedidos cargados (historial). */
export const agruparSemanas = (orders, cierres) => {
  const m = {};
  orders.forEach((o) => { const k = semanaDe(o.fecha); (m[k] = m[k] || []).push(o); });
  cierres.filter((c) => !c.reabierto).forEach((c) => { m[c.inicio] = m[c.inicio] || []; });
  return Object.keys(m).sort().reverse().map((ini) => ({ ini, fin: finDe(ini), orders: m[ini] }));
};

export const SemanaHeader = ({ s, cierre, scope, isAdmin, abierta, onToggle, onChanged, colSpan }) => {
  const [busy, setBusy] = useState(false);
  const t = cierre?.totales && cierre.vendedor === scope ? cierre.totales : totalesArticulos(s.orders);
  const q = `inicio=${s.ini}&vendedor=${encodeURIComponent(scope)}`;
  const nombre = `recibo_semanal_${s.ini}`;
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(formatApiError(e) || String(e.message || e)); } finally { setBusy(false); } };
  const cerrar = () => window.confirm(`¿Hacer el cierre semanal del ${fmt(s.ini)} al ${fmt(s.fin)}? (${t.pedidos} pedidos · ${t.articulos} artículos)`) &&
    run(async () => { await api.post("/mis-ventas/cierre-semanal", { inicio: s.ini, vendedor: scope }); toast.success("Semana cerrada"); onChanged(); });
  const reabrir = () => window.confirm("¿Reabrir este cierre semanal? Quedará registrado.") &&
    run(async () => { await api.post(`/mis-ventas/cierre-semanal/${cierre.id}/reabrir`); toast.success("Cierre reabierto"); onChanged(); });
  return (
    <tr className="bg-zinc-900 text-white border-t-4 border-[#002FA7]" data-testid={`semana-${s.ini}`}>
      <td colSpan={colSpan} className="p-0">
        <div className="sticky left-0 w-[calc(100vw-2rem)] md:w-auto px-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-2">
          <button onClick={onToggle} className="flex items-center gap-1 font-bold" data-testid={`semana-toggle-${s.ini}`}>
            {abierta ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />} Semana sáb {fmt(s.ini)} – vie {fmt(s.fin)}
          </button>
          <span className="text-xs" data-testid={`semana-totales-${s.ini}`}>{t.pedidos} pedidos · {t.articulos} artículos · {t.facturados} facturados · {t.pendientes} pendientes</span>
          {cierre ? <span className="text-xs bg-emerald-600 rounded px-2 py-0.5 flex items-center gap-1" data-testid={`semana-cerrada-${s.ini}`}><Lock className="w-3 h-3" /> Cerrada{cierre.vendedor === "general" ? " (general)" : ""} · {cierre.cerrado_por_nombre}</span>
            : <button disabled={busy} onClick={cerrar} className="text-xs bg-amber-400 text-black font-bold rounded px-2 py-1" data-testid={`semana-cerrar-${s.ini}`}>Cierre semanal</button>}
          <div className="ml-auto flex gap-1">
            {cierre && isAdmin && <button disabled={busy} onClick={reabrir} className="text-xs border border-white/40 rounded px-2 py-1 flex items-center gap-1" data-testid={`semana-reabrir-${s.ini}`}><Unlock className="w-3 h-3" /> Reabrir</button>}
            <button disabled={busy} onClick={() => run(() => imprimirPdf(`/mis-ventas/semana/reporte.pdf?${q}`))} className="text-xs border border-white/40 rounded px-2 py-1 flex items-center gap-1" data-testid={`semana-imprimir-${s.ini}`}><Printer className="w-3 h-3" /> Imprimir</button>
            <button disabled={busy} onClick={() => run(() => descargar(`/mis-ventas/semana/reporte.xlsx?${q}`, `${nombre}.xlsx`))} className="text-xs border border-white/40 rounded px-2 py-1 flex items-center gap-1" data-testid={`semana-xlsx-${s.ini}`}><FileSpreadsheet className="w-3 h-3" /> Excel</button>
            <button disabled={busy} onClick={() => run(() => descargar(`/mis-ventas/semana/reporte.pdf?${q}`, `${nombre}.pdf`))} className="text-xs border border-white/40 rounded px-2 py-1 flex items-center gap-1" data-testid={`semana-pdf-${s.ini}`}><FileText className="w-3 h-3" /> PDF</button>
          </div>
        </div>
      </td>
    </tr>
  );
};
